/**
 * V2.1 — staging must not feel like code: [Stage] opens a small popover
 * with the job pre-selected and the location as tappable bin chips, plus
 * free text with one confirm button. Tapping a chip stages in ONE tap.
 *
 * #830 (office audit 3 C4/C5/M10): the chips now come from the ORG-LEVEL bin
 * list (GET /api/tracked-parts/bins, org_settings-backed) instead of each
 * browser's localStorage — a typed sentence can never become a permanent
 * "location" again, and every coordinator sees the same bins. The dialog says
 * WHO reads the answer, the Stage button requires a location, and skipping is
 * an explicit stated choice, never an accident.
 */
import { useState, type ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";

/** Mirror of the server's seed list (trackedParts.ts DEFAULT_STAGING_BINS) —
 *  only used when the bins request itself fails, so chips never vanish. */
const FALLBACK_BINS = ["Will-call shelf", "David's bin", "Truck", "Receptionist desk"];

export function StagePartPopover({
    partCode,
    jobLabel,
    currentNote,
    pending,
    onStage,
    trigger,
}: {
    partCode: string;
    /** "#4123 — Chuy's Frisco" context line; the job is pre-selected. */
    jobLabel?: string | null;
    currentNote?: string | null;
    pending: boolean;
    onStage: (note: string | undefined) => void;
    trigger: ReactNode;
}) {
    const [open, setOpen] = useState(false);
    const [freeText, setFreeText] = useState("");
    const binsQ = useQuery({
        queryKey: ["staging-bins"],
        queryFn: () => api.get<{ bins: string[]; is_default: boolean }>(`/api/tracked-parts/bins`),
        staleTime: 5 * 60_000,
        enabled: open,
    });
    const bins = binsQ.data?.bins?.length ? binsQ.data.bins : FALLBACK_BINS;

    const stageWith = (note: string | undefined) => {
        onStage(note);
        setOpen(false);
        setFreeText("");
    };

    return (
        <Popover
            open={open}
            onOpenChange={(o) => {
                setOpen(o);
                if (o) setFreeText(currentNote ?? "");
            }}
        >
            <PopoverTrigger asChild>{trigger}</PopoverTrigger>
            <PopoverContent className="w-80 space-y-2.5 p-3" onClick={(e) => e.stopPropagation()}>
                <div>
                    <div className="text-sm font-semibold">
                        Stage <span className="font-mono">{partCode}</span>
                    </div>
                    <div className="text-xs text-muted-foreground">
                        {jobLabel ? `For ${jobLabel} — where is it?` : "Where is it?"}
                    </div>
                    {/* #830 C5: the field is read by a technician hunting for a
                        physical part — say so, so it never becomes a message
                        board again. */}
                    <div className="mt-1 text-xs text-foreground/70" data-testid="who-reads-this">
                        <span className="font-medium">Your technician reads this to find the part.</span> Say a real
                        place — notes for colleagues go on the part&apos;s timeline, not here.
                    </div>
                </div>
                <div className="flex flex-wrap gap-1.5">
                    {bins.map((bin) => (
                        <button
                            key={bin}
                            type="button"
                            disabled={pending}
                            className="rounded-full border bg-background px-2.5 py-1 text-xs hover:bg-violet-600 hover:text-white hover:border-violet-600 disabled:opacity-50"
                            onClick={() => stageWith(bin)}
                        >
                            {bin}
                        </button>
                    ))}
                </div>
                <form
                    className="flex items-center gap-1.5"
                    onSubmit={(e) => {
                        e.preventDefault();
                        // #830 C4: no silent blanks — Stage submits only with a
                        // real location; skipping goes through the explicit
                        // opt-out below.
                        if (freeText.trim()) stageWith(freeText.trim());
                    }}
                >
                    <Input
                        autoFocus
                        className="h-8 text-xs"
                        placeholder="Other location…"
                        value={freeText}
                        onChange={(e) => setFreeText(e.target.value)}
                    />
                    <Button
                        type="submit"
                        size="sm"
                        disabled={pending || !freeText.trim()}
                        className="h-8 shrink-0"
                        title={freeText.trim() ? undefined : "Pick a bin above or type where the part is"}
                    >
                        Stage
                    </Button>
                </form>
                <button
                    type="button"
                    disabled={pending}
                    data-testid="stage-no-location"
                    className="w-full text-left text-[11px] text-foreground/55 underline-offset-2 hover:text-foreground hover:underline disabled:opacity-50"
                    onClick={() => stageWith(undefined)}
                >
                    No location yet — the tech will have to ask
                </button>
            </PopoverContent>
        </Popover>
    );
}
